/**
 * RecognitionCelebration — the "You got a High Five!" moment (2026-06).
 *
 * Fires FULL-SCREEN the instant a crew member opens the app with an unseen
 * recognition, so the energy isn't lost and they see THEIRS first (not the
 * whole board). Confetti, the giver, the value they lived, and the note —
 * front and center. Cycles if they got several, then marks them seen so it
 * only celebrates once.
 *
 * Mounted once at the app root. Self-fetches on login + on foreground return,
 * and also listens for `wow:recognition-celebrate` (dispatched on a
 * notification tap) to replay on demand.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { HandHeart, ArrowRight, Sparkle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const abs = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : "");

const VALUE_COLORS = {
  wow_customer: "#2563EB",
  unleash_passion: "#EC4899",
  penny_hostage: "#16A34A",
  achieve_impossible: "#7C3AED",
  work_hard_fun: "#F59E0B",
};

function burst() {
  const base = { startVelocity: 42, spread: 360, ticks: 70, zIndex: 2147483647, disableForReducedMotion: true };
  const colors = ["#2563EB", "#EC4899", "#16A34A", "#7C3AED", "#F59E0B", "#ffffff"];
  confetti({ ...base, particleCount: 90, origin: { x: 0.5, y: 0.35 }, colors });
  setTimeout(() => confetti({ ...base, particleCount: 50, angle: 60, origin: { x: 0, y: 0.6 }, colors }), 150);
  setTimeout(() => confetti({ ...base, particleCount: 50, angle: 120, origin: { x: 1, y: 0.6 }, colors }), 150);
}

export default function RecognitionCelebration({ user, onExplore }) {
  const crewId = user?.crew_id;
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const checkingRef = useRef(false);

  const check = useCallback(async () => {
    if (!crewId || checkingRef.current || cards.length) return;
    checkingRef.current = true;
    try {
      const r = await axios.get(`${API}/recognition/unseen`, { params: { actor_id: crewId } });
      const found = r.data?.cards || [];
      if (found.length) { setCards(found); setIdx(0); }
    } catch (_) { /* ignore */ }
    finally { checkingRef.current = false; }
  }, [crewId, cards.length]);

  // Fire on login, on foreground return, and on explicit replay request.
  useEffect(() => {
    if (!crewId) return undefined;
    const t = setTimeout(check, 900); // let the boot splash settle first
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    const onReplay = () => check();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("wow:recognition-celebrate", onReplay);
    return () => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("wow:recognition-celebrate", onReplay);
    };
  }, [crewId, check]);

  // Confetti each time a card is shown.
  useEffect(() => {
    if (cards.length) burst();
  }, [cards.length, idx]);

  const ackAndClose = useCallback(async (explore) => {
    const ids = cards.map((c) => c.id);
    setCards([]); setIdx(0);
    try { await axios.post(`${API}/recognition/seen`, { actor_id: crewId, ids }); } catch (_) { /* ignore */ }
    if (explore && onExplore) onExplore();
  }, [cards, crewId, onExplore]);

  if (!cards.length) return null;
  const c = cards[idx] || {};
  const color = VALUE_COLORS[c.value_key] || "#2563EB";
  const more = idx < cards.length - 1;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.72)", backdropFilter: "blur(6px)" }}
      data-testid="recognition-celebration"
    >
      <div
        className="relative w-full max-w-sm rounded-[28px] overflow-hidden shadow-2xl"
        style={{ background: "linear-gradient(165deg,#1d4ed8 0%,#7c3aed 55%,#db2777 100%)", animation: "wowPop 420ms cubic-bezier(.17,.67,.3,1.33)" }}
      >
        <style>{`@keyframes wowPop{0%{transform:scale(.7);opacity:0}100%{transform:scale(1);opacity:1}}
          @keyframes wowFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}`}</style>

        <div className="px-6 pt-7 pb-6 text-center text-white">
          {cards.length > 1 && (
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-white/70 mb-1">{idx + 1} of {cards.length}</p>
          )}
          <div className="inline-flex items-center justify-center" style={{ animation: "wowFloat 2.4s ease-in-out infinite" }}>
            <HandHeart weight="fill" className="w-14 h-14 text-white drop-shadow" />
          </div>
          <h2 className="mt-2 text-3xl font-black leading-tight" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            You got a High&nbsp;Five!
          </h2>
          <p className="text-white/80 text-sm font-bold mt-1">
            {c.from_name ? `${c.from_name} recognized you` : "Someone recognized you"}
          </p>

          {/* value chip */}
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow" style={{ color }}>
            <span className="text-lg">{c.value_emoji}</span>
            <span className="font-black text-sm">{c.value_label}</span>
          </div>

          {c.note && (
            <div className="mt-4 bg-white/15 rounded-2xl px-4 py-3">
              <p className="text-white text-[15px] leading-snug">“{c.note}”</p>
            </div>
          )}

          <div className="mt-6 space-y-2">
            {more ? (
              <button
                onClick={() => setIdx((i) => i + 1)}
                className="w-full rounded-full bg-white text-slate-900 font-black py-3 inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                data-testid="recognition-celebration-next"
              >
                See the next one <ArrowRight weight="bold" className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => ackAndClose(true)}
                className="w-full rounded-full bg-white text-slate-900 font-black py-3 inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                data-testid="recognition-celebration-explore"
              >
                <Sparkle weight="fill" className="w-4 h-4" /> See the Recognition Board
              </button>
            )}
            <button
              onClick={() => (more ? setIdx((i) => i + 1) : ackAndClose(false))}
              className="w-full rounded-full bg-white/10 text-white font-bold py-2.5 active:scale-[0.98] transition-transform"
              data-testid="recognition-celebration-close"
            >
              {more ? "Skip" : "Thanks! 🙌"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
